import type { Placement } from '@popperjs/core';
import { Button, SideEnum, type ButtonProps } from '@talos/kyoko';
import { useEffect, useRef, useState } from 'react';

import { RFQSweepingPanel } from './RFQSweepingPanel';
import type { RFQSweepingSharedProps } from './types';
import { useRFQSweepingPanel } from './useRFQSweepingPanel';

interface RFQSweepingButtonProps extends RFQSweepingSharedProps, Omit<ButtonProps, 'onClick'> {
  /** Preferred side to show when opening the sweeper panel. */
  side?: SideEnum;
  /** Popper offset applied to the sweeper panel dropdown. */
  panelOffset?: number;
  /** Popper placement for the sweeper panel dropdown. */
  panelPlacement?: Placement;
}

export function RFQSweepingButton({
  correlationID,
  side = SideEnum.Buy,
  buyDisabled,
  sellDisabled,
  associatedCareOrder,
  panelOffset,
  panelPlacement,
  children = 'Sweep',
  disabled,
  ...buttonProps
}: RFQSweepingButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpenedOnce, setHasBeenOpenedOnce] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  // Default to sell when buys are unavailable but sells can still be swept.
  const initialSide = buyDisabled && !sellDisabled ? SideEnum.Sell : side;
  // Disable the button when requested or when neither side can be swept.
  const isDisabled = disabled || (buyDisabled && sellDisabled);

  const dropdownProps = useRFQSweepingPanel({
    isOpen,
    close: () => setIsOpen(false),
    panelAnchorRef: anchorRef,
    panelOffset,
    panelPlacement,
  });

  // We only render the Sweeper Panel (containing its non-trivial blotters) once it has been opened _once_
  useEffect(() => {
    setHasBeenOpenedOnce(curr => curr || dropdownProps.isOpen);
  }, [dropdownProps.isOpen]);

  return (
    <div ref={anchorRef}>
      <Button {...buttonProps} disabled={isDisabled} onClick={() => setIsOpen(true)}>
        {children}
      </Button>

      {hasBeenOpenedOnce && (
        <RFQSweepingPanel
          correlationID={correlationID}
          initialSide={initialSide}
          buyDisabled={buyDisabled}
          sellDisabled={sellDisabled}
          associatedCareOrder={associatedCareOrder}
          dropdownProps={dropdownProps}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
